import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Tabs,
    Descriptions,
    Tag,
    Badge,
    Typography,
    Card,
    Button,
    Input,
    Space,
    Avatar,
    message,
    Empty,
    Popconfirm,
    Divider,
    Select,
    theme,
} from 'antd';
import {
    PlusOutlined,
    SendOutlined,
    DeleteOutlined,
    EditOutlined,
    SaveOutlined,
    CloseOutlined,
    RocketOutlined,
    WarningOutlined,
    CheckCircleOutlined,
    InfoCircleOutlined,
    CommentOutlined,
} from '@ant-design/icons';
import { strategyApi } from '../../api/client';
import RichMentionEditor from '../../components/common/RichMentionEditor';
import MentionDisplay from '../../components/common/MentionDisplay';
import { useNotificationRefresh } from '../../hooks/useNotificationRefresh';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pl';

dayjs.extend(relativeTime);
dayjs.locale('pl');

const { Text } = Typography;
const { TextArea } = Input;

// Constants - synced with StrategyPage.tsx
const PRIORITIES = [
    { value: 'P0', label: 'P0 — Krytyczna', color: 'red' },
    { value: 'P1', label: 'P1 — Ważna', color: 'orange' },
    { value: 'P2', label: 'P2 — Nice-to-have', color: 'blue' },
];

const INITIATIVE_STATUSES = [
    { value: 'PLANNED', label: 'Planowana', color: 'default' },
    { value: 'ACTIVE', label: 'Aktywna', color: 'processing' },
    { value: 'ON_HOLD', label: 'Wstrzymana', color: 'warning' },
    { value: 'CLOSED', label: 'Zamknięta', color: 'success' },
    { value: 'ABANDONED', label: 'Porzucona', color: 'error' },
];

const LOAD_LEVELS = [
    { value: 'LOW', label: 'Niska', color: 'green' },
    { value: 'MEDIUM', label: 'Średnia', color: 'orange' },
    { value: 'HIGH', label: 'Wysoka', color: 'red' },
];

// AREAS constant synced with StrategyPage.tsx
const AREAS = [
    { value: 'sprzedaz', label: '💰 Sprzedaż' },
    { value: 'produkt', label: '📦 Produkt' },
    { value: 'IT', label: '💻 IT' },
    { value: 'logistyka', label: '🚚 Logistyka' },
    { value: 'finanse', label: '📊 Finanse' },
    { value: 'marketing', label: '📣 Marketing' },
];

const UPDATE_TYPES = [
    { value: 'PROGRESS', label: '🟢 Postęp', color: '#52c41a', icon: <RocketOutlined /> },
    { value: 'BLOCKER', label: '🔴 Blokada', color: '#f5222d', icon: <WarningOutlined /> },
    { value: 'MILESTONE', label: '🎯 Kamień milowy', color: '#1890ff', icon: <CheckCircleOutlined /> },
    { value: 'NOTE', label: '📝 Notatka', color: '#8c8c8c', icon: <InfoCircleOutlined /> },
];

interface Initiative {
    id: string;
    name: string;
    priority: string;
    status: string;
    mode: string;
    areas?: string[];
    horizon?: string;
    estimatedLoad?: string;
    businessObjective?: string;
    successMetrics?: string;
    goNoGoCriteria?: string;
    startDate?: string;
    endDate?: string;
    notes?: string;
    owner?: { id: string; firstName: string; lastName: string };
    goal?: { name: string };
    milestones?: { id: string; name: string; dueDate: string; isCompleted: boolean }[];
}

interface Update {
    id: string;
    type: string;
    content: string;
    createdAt: string;
    author: { id: string; firstName: string; lastName: string };
}

interface Comment {
    id: string;
    content: string;
    createdAt: string;
    author: { id: string; firstName: string; lastName: string };
    mentionedUserIds?: string[];
}

interface Props {
    initiative: Initiative;
    onTabChange?: (key: string) => void;
}

export default function InitiativeDetailTabs({ initiative, onTabChange }: Props) {
    const queryClient = useQueryClient();
    const { token } = theme.useToken();

    // Update form state
    const [newUpdateType, setNewUpdateType] = useState<string>('PROGRESS');
    const [newUpdateContent, setNewUpdateContent] = useState('');
    const [editingUpdateId, setEditingUpdateId] = useState<string | null>(null);
    const [editingUpdateContent, setEditingUpdateContent] = useState('');

    // Comment form state
    const [commentText, setCommentText] = useState('');
    const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
    const [editingCommentContent, setEditingCommentContent] = useState('');

    // Queries
    const { data: updates = [] } = useQuery<Update[]>({
        queryKey: ['initiative-updates', initiative.id],
        queryFn: () => strategyApi.getUpdates(initiative.id).then((r: any) => r.data),
    });

    const { data: comments = [] } = useQuery<Comment[]>({
        queryKey: ['initiative-comments', initiative.id],
        queryFn: () => strategyApi.getComments(initiative.id).then((r: any) => r.data),
    });

    const refreshNotifications = useNotificationRefresh();

    // Mutations
    const createUpdateMutation = useMutation({
        mutationFn: (data: any) => strategyApi.createUpdate(initiative.id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['initiative-updates', initiative.id] });
            refreshNotifications();
            setNewUpdateContent('');
            message.success('Aktualizacja dodana');
        },
    });

    const createCommentMutation = useMutation({
        mutationFn: (data: any) => strategyApi.createComment(initiative.id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['initiative-comments', initiative.id] });
            refreshNotifications();
            setCommentText('');
            message.success('Komentarz dodany');
        },
    });

    const deleteUpdateMutation = useMutation({
        mutationFn: (id: string) => strategyApi.deleteUpdate(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['initiative-updates', initiative.id] });
            refreshNotifications();
            message.success('Usunięto');
        },
    });

    const deleteCommentMutation = useMutation({
        mutationFn: (id: string) => strategyApi.deleteComment(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['initiative-comments', initiative.id] });
            refreshNotifications();
            message.success('Usunięto');
        },
    });

    const updateInitiativeMutation = useMutation({
        mutationFn: (data: any) => strategyApi.updateInitiative(initiative.id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['strategy-initiatives'] });
            queryClient.invalidateQueries({ queryKey: ['strategy-overview'] }); // Update stats
            message.success('Zaktualizowano status');
        },
        onError: () => message.error('Błąd aktualizacji statusu'),
    });

    const handleStatusChange = (value: string) => {
        updateInitiativeMutation.mutate({ status: value });
    };

    // Handlers
    const handleAddUpdate = () => {
        if (!newUpdateContent.trim()) return;
        createUpdateMutation.mutate({ type: newUpdateType, content: newUpdateContent });
    };

    const handleAddComment = () => {
        if (!commentText.trim()) return;

        // Extract mentioned user IDs
        const mentionedUserIds: string[] = [];
        const regex = /@([^\[]+)\[([^\]]+)\]/g;
        let match;
        while ((match = regex.exec(commentText)) !== null) {
            mentionedUserIds.push(match[2]);
        }

        createCommentMutation.mutate({
            content: commentText,
            mentionedUserIds: mentionedUserIds.length > 0 ? mentionedUserIds : undefined
        });
    };

    const getUpdateTypeInfo = (type: string) => UPDATE_TYPES.find(t => t.value === type) || UPDATE_TYPES[3];

    const handleTabChange = (key: string) => {
        onTabChange?.(key);
    };

    // Render areas as tags
    const renderAreas = () => {
        if (!initiative.areas || initiative.areas.length === 0) return '-';
        return initiative.areas.map((a, idx) => {
            const areaInfo = AREAS.find(x => x.value === a);
            return <Tag key={idx}>{areaInfo?.label || a}</Tag>;
        });
    };

    // ==================== MAIN RENDER ====================
    return (
        <div style={{ display: 'flex', gap: 16 }}>
            {/* Left column - Info & Updates tabs (flex: 1 to take remaining space) */}
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <Tabs defaultActiveKey="info" onChange={handleTabChange}>
                    {/* ==================== INFO TAB ==================== */}
                    <Tabs.TabPane tab="📋 Info" key="info">
                        <Descriptions bordered column={2} size="small">
                            <Descriptions.Item label="Nazwa" span={2}>
                                <Text strong>{initiative.name}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Priorytet">
                                <Tag color={PRIORITIES.find(p => p.value === initiative.priority)?.color}>
                                    {initiative.priority}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Select
                                    defaultValue={initiative.status}
                                    style={{ width: 140 }}
                                    onChange={handleStatusChange}
                                    options={INITIATIVE_STATUSES.map(s => ({
                                        value: s.value,
                                        label: <Tag color={s.color}>{s.label}</Tag>
                                    }))}
                                    variant="borderless"
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="Mode">
                                <Tag color={initiative.mode === 'CORE' ? 'volcano' : 'cyan'}>
                                    {initiative.mode}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Obszary">
                                {renderAreas()}
                            </Descriptions.Item>
                            <Descriptions.Item label="Horyzont">{initiative.horizon || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Obciążenie">
                                {LOAD_LEVELS.find(l => l.value === initiative.estimatedLoad)?.label || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Cel biznesowy" span={2}>
                                <span style={{ whiteSpace: 'pre-wrap' }}>{initiative.businessObjective || '-'}</span>
                            </Descriptions.Item>
                            <Descriptions.Item label="Metryki sukcesu" span={2}>
                                <div className="ql-editor" style={{ padding: 0 }} dangerouslySetInnerHTML={{ __html: initiative.successMetrics || '-' }} />
                            </Descriptions.Item>
                            <Descriptions.Item label="Kryteria Go/No-Go" span={2}>
                                <div className="ql-editor" style={{ padding: 0 }} dangerouslySetInnerHTML={{ __html: initiative.goNoGoCriteria || '-' }} />
                            </Descriptions.Item>
                            <Descriptions.Item label="Notatki" span={2}>
                                {initiative.notes ? (
                                    <div className="ql-editor" style={{ padding: 0 }} dangerouslySetInnerHTML={{ __html: initiative.notes }} />
                                ) : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Właściciel">
                                {initiative.owner ? `${initiative.owner.firstName} ${initiative.owner.lastName}` : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Data startu">
                                {initiative.startDate ? dayjs(initiative.startDate).format('DD.MM.YYYY') : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Data końca">
                                {initiative.endDate ? dayjs(initiative.endDate).format('DD.MM.YYYY') : '-'}
                            </Descriptions.Item>
                            {initiative.goal && (
                                <Descriptions.Item label="Cel strategiczny" span={2}>
                                    <Tag color="blue">{initiative.goal.name}</Tag>
                                </Descriptions.Item>
                            )}
                            {initiative.milestones && initiative.milestones.length > 0 && (
                                <Descriptions.Item label="Kamienie milowe" span={2}>
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                        {initiative.milestones.map(m => (
                                            <li key={m.id}>
                                                <Badge status={m.isCompleted ? 'success' : 'default'} />
                                                {m.name} — {dayjs(m.dueDate).format('DD.MM.YYYY')}
                                            </li>
                                        ))}
                                    </ul>
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </Tabs.TabPane>

                    {/* ==================== UPDATES TAB ==================== */}
                    <Tabs.TabPane tab={`📝 Aktualizacje (${updates.length})`} key="updates">
                        {/* Add form */}
                        <Card size="small" style={{ marginBottom: 16, background: token.colorFillAlter }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Space wrap>
                                    {UPDATE_TYPES.map(t => (
                                        <Button
                                            key={t.value}
                                            size="small"
                                            type={newUpdateType === t.value ? 'primary' : 'default'}
                                            onClick={() => setNewUpdateType(t.value)}
                                            style={{ borderColor: newUpdateType === t.value ? t.color : undefined }}
                                        >
                                            {t.label}
                                        </Button>
                                    ))}
                                </Space>
                                <RichMentionEditor
                                    value={newUpdateContent}
                                    onChange={setNewUpdateContent}
                                    placeholder="Opisz postęp, problem lub ważną informację... Wpisz @ aby oznaczyć osobę"
                                    minRows={2}
                                />
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={handleAddUpdate}
                                    loading={createUpdateMutation.isPending}
                                    disabled={!newUpdateContent.trim()}
                                >
                                    Dodaj aktualizację
                                </Button>
                            </Space>
                        </Card>

                        {/* Updates list */}
                        {updates.length === 0 ? (
                            <Empty description="Brak aktualizacji" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 300, overflowY: 'auto' }}>
                                {updates.map(update => {
                                    const typeInfo = getUpdateTypeInfo(update.type);
                                    const isEditing = editingUpdateId === update.id;

                                    return (
                                        <Card
                                            key={update.id}
                                            size="small"
                                            style={{
                                                borderLeft: `4px solid ${typeInfo.color}`,
                                                background: isEditing ? token.colorWarningBg : undefined,
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                                <Space>
                                                    <Tag color={typeInfo.color} style={{ borderRadius: 12 }}>
                                                        {typeInfo.label}
                                                    </Tag>
                                                    <Avatar size="small" style={{ backgroundColor: token.colorPrimary }}>
                                                        {update.author.firstName[0]}
                                                    </Avatar>
                                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                                        {update.author.firstName} {update.author.lastName}
                                                    </Text>
                                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                                        • {dayjs(update.createdAt).fromNow()}
                                                    </Text>
                                                </Space>
                                                <Space size={4}>
                                                    {!isEditing && (
                                                        <>
                                                            <Button
                                                                size="small"
                                                                type="text"
                                                                icon={<EditOutlined />}
                                                                onClick={() => {
                                                                    setEditingUpdateId(update.id);
                                                                    setEditingUpdateContent(update.content);
                                                                }}
                                                            />
                                                            <Popconfirm
                                                                title="Usunąć aktualizację?"
                                                                onConfirm={() => deleteUpdateMutation.mutate(update.id)}
                                                            >
                                                                <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                                                            </Popconfirm>
                                                        </>
                                                    )}
                                                </Space>
                                            </div>

                                            {isEditing ? (
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                    <TextArea
                                                        value={editingUpdateContent}
                                                        onChange={e => setEditingUpdateContent(e.target.value)}
                                                        rows={3}
                                                        autoFocus
                                                    />
                                                    <Space>
                                                        <Button
                                                            size="small"
                                                            type="primary"
                                                            icon={<SaveOutlined />}
                                                            onClick={() => {
                                                                message.info('Edycja będzie dostępna wkrótce');
                                                                setEditingUpdateId(null);
                                                            }}
                                                        >
                                                            Zapisz
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            icon={<CloseOutlined />}
                                                            onClick={() => setEditingUpdateId(null)}
                                                        >
                                                            Anuluj
                                                        </Button>
                                                    </Space>
                                                </Space>
                                            ) : (
                                                <div style={{ marginBottom: 0 }}>
                                                    <MentionDisplay content={update.content} />
                                                </div>
                                            )}
                                        </Card>
                                    );
                                })}
                            </div>
                        )}
                    </Tabs.TabPane>
                </Tabs>
            </div>

            {/* Right column - Comments sidebar (fixed width, always visible like Trello) */}
            <div style={{
                width: 300,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                background: token.colorFillQuaternary,
                borderRadius: 8,
                padding: 12,
                maxHeight: 500,
            }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <CommentOutlined style={{ fontSize: 14 }} />
                    <Text strong>Komentarze</Text>
                    <Tag style={{ marginLeft: 'auto' }}>{comments.length}</Tag>
                </div>

                {/* Comment input at top - like Trello */}
                <div style={{ marginBottom: 12 }}>
                    <RichMentionEditor
                        value={commentText}
                        onChange={setCommentText}
                        placeholder="Napisz komentarz... (@mention)"
                        minRows={2}
                        onPressEnter={handleAddComment}
                    />
                    <Button
                        type="primary"
                        size="small"
                        icon={<SendOutlined />}
                        onClick={handleAddComment}
                        loading={createCommentMutation.isPending}
                        disabled={!commentText.trim()}
                        style={{ marginTop: 8 }}
                    >
                        Wyślij
                    </Button>
                </div>

                <Divider style={{ margin: '8px 0' }} />

                {/* Comments list - scrollable with fixed height */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                }}>
                    {comments.length === 0 ? (
                        <Empty
                            description={<Text type="secondary">Brak komentarzy</Text>}
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            style={{ margin: 'auto' }}
                        />
                    ) : (
                        comments.map(comment => {
                            const isEditing = editingCommentId === comment.id;

                            return (
                                <div
                                    key={comment.id}
                                    style={{
                                        display: 'flex',
                                        gap: 8,
                                        background: token.colorBgContainer,
                                        padding: 10,
                                        borderRadius: 6,
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                                    }}
                                >
                                    <Avatar size="small" style={{ backgroundColor: token.colorPrimary, flexShrink: 0 }}>
                                        {comment.author.firstName[0]}
                                    </Avatar>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                                            <div>
                                                <Text strong style={{ fontSize: 12 }}>
                                                    {comment.author.firstName} {comment.author.lastName}
                                                </Text>
                                                <br />
                                                <Text type="secondary" style={{ fontSize: 11 }}>
                                                    {dayjs(comment.createdAt).fromNow()}
                                                </Text>
                                            </div>
                                            {!isEditing && (
                                                <Space size={0}>
                                                    <Button
                                                        size="small"
                                                        type="text"
                                                        icon={<EditOutlined style={{ fontSize: 11 }} />}
                                                        onClick={() => {
                                                            setEditingCommentId(comment.id);
                                                            setEditingCommentContent(comment.content);
                                                        }}
                                                        style={{ padding: '0 4px', height: 20 }}
                                                    />
                                                    <Popconfirm
                                                        title="Usunąć komentarz?"
                                                        onConfirm={() => deleteCommentMutation.mutate(comment.id)}
                                                    >
                                                        <Button size="small" type="text" danger icon={<DeleteOutlined style={{ fontSize: 11 }} />} style={{ padding: '0 4px', height: 20 }} />
                                                    </Popconfirm>
                                                </Space>
                                            )}
                                        </div>

                                        {isEditing ? (
                                            <Space direction="vertical" style={{ width: '100%' }} size={4}>
                                                <TextArea
                                                    value={editingCommentContent}
                                                    onChange={e => setEditingCommentContent(e.target.value)}
                                                    rows={2}
                                                    autoFocus
                                                    size="small"
                                                />
                                                <Space size={4}>
                                                    <Button
                                                        size="small"
                                                        type="primary"
                                                        icon={<SaveOutlined />}
                                                        onClick={() => {
                                                            message.info('Edycja będzie dostępna wkrótce');
                                                            setEditingCommentId(null);
                                                        }}
                                                    >
                                                        Zapisz
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        icon={<CloseOutlined />}
                                                        onClick={() => setEditingCommentId(null)}
                                                    >
                                                        Anuluj
                                                    </Button>
                                                </Space>
                                            </Space>
                                        ) : (
                                            <div style={{ fontSize: 13 }}>
                                                <MentionDisplay content={comment.content} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}
